import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { PlanPicker } from './SettingsPage';
import { companiesApi } from '@/lib/companies';
import { billingApi } from '@/lib/billing';
import { api } from '@/lib/api';

/**
 * Dedicated in-app subscription page — the single upgrade destination that every
 * payment wall and the trial banner link to. Lists all plans + per-tier features
 * + prices and lets the owner/admin check out via Stripe (reuses the PlanPicker).
 */
export function SubscriptionPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const [upgradeErr, setUpgradeErr] = useState<string | null>(null);

  const { data: workspace } = useQuery({
    queryKey: ['workspace', wsId],
    queryFn: async () => (await api.get<{ companyId: string }>(`/api/workspaces/${wsId}`)).data,
    enabled: !!wsId,
  });
  const companyId = workspace?.companyId;

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesApi.list(),
  });
  const company = companies.find((c) => c.id === companyId);

  const { data: trial } = useQuery({
    queryKey: ['billing-trial', companyId],
    queryFn: () => billingApi.trial(companyId!),
    enabled: !!companyId,
  });

  return (
    <AppShell title={t('subscription.title', { defaultValue: 'Subscription' })} subtitle={t('subscription.subtitle', { defaultValue: 'Choose the plan that fits your team.' })}>
      <main className="mx-auto max-w-4xl px-6 py-10 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">{t('subscription.title', { defaultValue: 'Subscription' })}</h1>
          <p className="mt-1 text-ink-600">{t('subscription.subtitle', { defaultValue: 'Choose the plan that fits your team.' })}</p>
        </div>

        {trial?.onTrial && (
          <div className="rounded-xl border border-accent-200 bg-accent-50 px-5 py-4 flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-accent-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-ink-900">
                {t('subscription.trial_active', { defaultValue: `You're on the free trial — full Growth features for ${trial.daysLeft} more day${trial.daysLeft === 1 ? '' : 's'}.`, count: trial.daysLeft })}
              </p>
              <p className="mt-0.5 text-sm text-ink-600">
                {t('subscription.trial_cta', { defaultValue: 'Pick a plan now to keep everything when the trial ends — no interruption.' })}
              </p>
            </div>
          </div>
        )}

        {companyId ? (
          <PlanPicker
            currentPlan={(company?.plan ?? 'free') as 'free' | 'growth' | 'enterprise'}
            companyId={companyId}
            error={upgradeErr}
            setError={setUpgradeErr}
            currentInterval={company?.subscriptionInterval ?? null}
            subscriptionEndsAt={company?.subscriptionEndsAt ?? null}
          />
        ) : (
          <p className="text-sm text-ink-500">{t('common.loading')}</p>
        )}
      </main>
    </AppShell>
  );
}
